/**
 * Step 2: Financial Assessment
 * Collects financial information for ROI calculations and solution sizing
 */

import React from 'react';
import { DollarSign, Building, Shield, TrendingUp } from 'lucide-react';
import { StepComponentProps } from '../../../../../types/onboarding';
import { InfoTooltip } from '../../../../shared/tooltip/InfoTooltip';
import { getTooltipContent } from '../../../../../constants/onboarding';

export const Step2FinancialAssessment: React.FC<StepComponentProps> = ({
  formData,
  errors,
  onFieldChange,
  progressiveDisclosure,
}) => {
  const handleBankingRelationshipChange = (bank: string, checked: boolean) => {
    const currentBanks = (formData.bankingRelationships as string[]) || [];
    if (checked) {
      onFieldChange('bankingRelationships', [...currentBanks, bank]);
    } else {
      onFieldChange(
        'bankingRelationships',
        currentBanks.filter((b) => b !== bank),
      );

      // Clear custom bank name if "Other" is unchecked
      if (bank === 'Other') {
        onFieldChange('customBankName', '');
      }
    }
  };

  // Show banking questions for larger communities or higher budgets
  const showBankingQuestions = () => {
    const units = parseInt(formData.totalUnits) || 0;
    const annualBudget = parseFloat(String(formData.annualBudget || 0));
    return units >= 200 || annualBudget >= 500000;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Helpers to show comma-separated numbers in text inputs while keeping numeric values in form state
  const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

  const formatNumberForInput = (value?: number | string | null) => {
    if (value === undefined || value === null || value === '') return '';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (Number.isNaN(num)) return '';
    return numberFormatter.format(Math.round(num));
  };

  const parseNumberFromInput = (raw: string) => {
    // Remove anything that's not digit or dot or minus. Then parse.
    const cleaned = raw.replace(/[^0-9.-]+/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') return 0;
    const parsed = parseFloat(cleaned);
    if (Number.isNaN(parsed)) return 0;
    return parsed;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <DollarSign className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Financial Assessment</h2>
        <p className="text-lg text-slate-600">
          Understanding your community's financial position helps us calculate accurate ROI
          projections
        </p>
      </div>

      <div className="space-y-6">
        {/* Treasury & Financial Overview */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            Financial Overview
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                Current Treasury/Reserve Amount *
                <InfoTooltip
                  title="Treasury/Reserve Amount"
                  content="This includes all cash reserves, savings accounts, and liquid assets available to your community. Used to calculate investment potential and ROI projections."
                  iconSize={14}
                />
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9,]*"
                value={formatNumberForInput(formData.treasuryAmount as number)}
                onChange={(e) =>
                  onFieldChange('treasuryAmount', parseNumberFromInput(e.target.value))
                }
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  errors.treasuryAmount ? 'border-red-500' : 'border-slate-300'
                }`}
                placeholder="e.g., 150,000"
              />
              {errors.treasuryAmount && (
                <p className="mt-1 text-sm text-red-600">{errors.treasuryAmount}</p>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                Monthly HOA Fee (per unit)
                <InfoTooltip
                  {...getTooltipContent('monthly-hoa-fee')}
                  content={
                    getTooltipContent('monthly-hoa-fee')?.content ||
                    'The regular monthly fee each unit owner pays to the association.'
                  }
                  iconSize={14}
                />
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9,]*"
                value={formatNumberForInput(formData.monthlyHOAFees as number)}
                onChange={(e) =>
                  onFieldChange('monthlyHOAFees', parseNumberFromInput(e.target.value))
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="e.g., 250"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                Annual Budget
                <InfoTooltip
                  {...getTooltipContent('annual-budget')}
                  content={
                    getTooltipContent('annual-budget')?.content ||
                    "Your community's total annual operating budget including all income and expenses."
                  }
                  iconSize={14}
                />
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9,]*"
                value={formatNumberForInput(formData.annualBudget as number)}
                onChange={(e) =>
                  onFieldChange('annualBudget', parseNumberFromInput(e.target.value))
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="e.g., 500,000"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                Emergency/Capital Reserve Funds
                <InfoTooltip
                  {...getTooltipContent('reserve-funds')}
                  content={
                    getTooltipContent('reserve-funds')?.content ||
                    'Money set aside for major repairs, replacements, and unexpected expenses.'
                  }
                  iconSize={14}
                />
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9,]*"
                value={formatNumberForInput(formData.reserveFunds as number)}
                onChange={(e) =>
                  onFieldChange('reserveFunds', parseNumberFromInput(e.target.value))
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="e.g., 100,000"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                Outstanding Debts/Loans
                <InfoTooltip
                  {...getTooltipContent('outstanding-debts')}
                  content={
                    getTooltipContent('outstanding-debts')?.content ||
                    'Any money your association currently owes to lenders or creditors.'
                  }
                  iconSize={14}
                />
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9,]*"
                value={formatNumberForInput(formData.outstandingDebts as number)}
                onChange={(e) =>
                  onFieldChange('outstandingDebts', parseNumberFromInput(e.target.value))
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="e.g., 50,000"
              />
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="investmentAccounts"
                checked={(formData.investmentAccounts as boolean) || false}
                onChange={(e) => onFieldChange('investmentAccounts', e.target.checked)}
                className="text-green-600 focus:ring-green-500"
              />
              <label htmlFor="investmentAccounts" className="text-sm text-slate-700">
                Currently have investment accounts (CDs, money market, etc.)
              </label>
            </div>
          </div>
        </div>

        {/* Banking Relationships - Conditional */}
        {showBankingQuestions() && (
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Banking Relationships
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Current banking partners (important for advanced integration features)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                'Bank of America',
                'Wells Fargo',
                'Chase',
                'US Bank',
                'PNC Bank',
                'Local Credit Union',
                'Community Bank',
                'Other',
              ].map((bank) => (
                <label key={bank} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={(formData.bankingRelationships as string[])?.includes(bank) || false}
                    onChange={(e) => handleBankingRelationshipChange(bank, e.target.checked)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">{bank}</span>
                </label>
              ))}
            </div>

            {/* Custom Bank Name Input - Shows when "Other" is selected */}
            {(formData.bankingRelationships as string[])?.includes('Other') && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Specify Other Bank Name *
                </label>
                <input
                  type="text"
                  value={formData.customBankName || ''}
                  onChange={(e) => onFieldChange('customBankName', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., First Community Bank, Local Savings & Loan"
                />
                {errors.customBankName && (
                  <p className="mt-1 text-sm text-red-600">{errors.customBankName}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Budget Status */}
        <div>
          <label className="flex items-center gap-2 text-lg font-semibold text-slate-800 mb-3">
            Budget Approval Status *
            <InfoTooltip
              title="Budget Status"
              content="Understanding your current budget situation helps us tailor our approach and timeline recommendations to match your approval process."
              iconSize={16}
            />
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Budget already approved',
              'Budget approval pending',
              'Still exploring costs',
              'Budget flexible based on value',
              'Need ROI justification for approval',
              'Requires board approval',
              'Considering phased budget approach',
            ].map((status) => (
              <label
                key={status}
                className="flex items-center p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="radio"
                  name="budgetStatus"
                  value={status}
                  checked={formData.budgetStatus === status}
                  onChange={(e) => onFieldChange('budgetStatus', e.target.value)}
                  className="mr-3 text-green-600 focus:ring-green-500"
                />
                <span className="text-slate-700">{status}</span>
              </label>
            ))}
          </div>
          {errors.budgetStatus && (
            <p className="mt-1 text-sm text-red-600">{errors.budgetStatus}</p>
          )}
        </div>

        {/* ROI Preview */}
        {formData.treasuryAmount && (formData.treasuryAmount as number) > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h4 className="font-semibold text-green-800 mb-4 text-center">
              Potential Annual Impact Preview
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-green-700">
                  {formatCurrency((formData.treasuryAmount as number) * 0.05)}
                </div>
                <div className="text-sm text-green-600">Conservative Investment Returns (5%)</div>
              </div>
              <div>
                <div className="text-lg font-bold text-green-700">
                  {formatCurrency(((formData.monthlyHOAFees as number) || 0) * 12 * 0.15)}
                </div>
                <div className="text-sm text-green-600">Estimated Cost Savings (15%)</div>
              </div>
              <div>
                <div className="text-lg font-bold text-green-700">
                  {formatCurrency(
                    (formData.treasuryAmount as number) * 0.05 +
                      ((formData.monthlyHOAFees as number) || 0) * 12 * 0.15,
                  )}
                </div>
                <div className="text-sm text-green-600">Total Potential Annual Benefit</div>
              </div>
            </div>
            <p className="text-xs text-green-600 text-center mt-3">
              *Conservative estimates based on 5% investment returns and 15% operational savings
            </p>
          </div>
        )}
      </div>

      {/* Information Panel */}
      <div className="bg-green-50 rounded-lg p-6 mt-6">
        <div className="flex items-start space-x-3">
          <Building className="w-6 h-6 text-green-600 mt-1" />
          <div>
            <h3 className="font-semibold text-green-900 mb-2">Why Financial Information Matters</h3>
            <ul className="text-sm text-green-800 space-y-1">
              <li>• Calculate realistic ROI projections specific to your community</li>
              <li>• Identify the best investment opportunities for your treasury</li>
              <li>• Recommend appropriate banking partnerships for advanced features</li>
              <li>• Structure implementation timeline based on your budget capacity</li>
              <li>• Provide accurate cost-benefit analysis for your board presentations</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
